"use client";
import { useSyncExternalStore } from "react";
import {
  BS_THEME_ATTR,
  DARK_MEDIA_QUERY,
  isThemeMode,
  THEME_ATTR,
  THEME_STORAGE_KEY,
  type ThemeMode,
} from "@/utils/theme-mode/theme-mode";

// Module-level store, not a Jotai atom: the source of truth is the <html> attribute the
// pre-paint boot script already set, so every toggle instance (PC header, mobile header,
// a remounted header) reads the same DOM value and can never disagree or duplicate state.

// The shopper's explicit choice for THIS document. Mirrors storage, and is the only
// memory of the choice when storage is blocked (switching must still work for the visit).
let manualChoice: ThemeMode | null = null;
const listeners = new Set<() => void>();

const readStoredMode = (): ThemeMode | null => {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(value) ? value : null;
  } catch {
    return null;
  }
};

const systemMode = (): ThemeMode =>
  window.matchMedia?.(DARK_MEDIA_QUERY).matches ? "dark" : "light";

const applyMode = (mode: ThemeMode) => {
  const root = document.documentElement;
  if (root.getAttribute(THEME_ATTR) === mode) return;
  // Suppress CSS transitions for the frame the palette flips in — otherwise every
  // element with a `transition: background …` animates at its own speed and the page
  // visibly smears from one theme to the other (and costs extra frames on weak phones).
  root.setAttribute("data-theme-switching", "");
  root.setAttribute(THEME_ATTR, mode);
  root.setAttribute(BS_THEME_ATTR, mode);
  requestAnimationFrame(() =>
    requestAnimationFrame(() => root.removeAttribute("data-theme-switching")),
  );
  listeners.forEach((notify) => notify());
};

export const getCurrentThemeMode = (): ThemeMode | null => {
  const value = document.documentElement.getAttribute(THEME_ATTR);
  return isThemeMode(value) ? value : null;
};

export const setThemeMode = (mode: ThemeMode) => {
  manualChoice = mode;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Storage blocked (private mode / some in-app browsers): `manualChoice` carries it.
  }
  applyMode(mode);
};

export const toggleThemeMode = () =>
  setThemeMode(getCurrentThemeMode() === "dark" ? "light" : "dark");

/** Device theme changed: follow it only while the shopper never chose manually. */
export const syncWithSystemMode = () => {
  if (manualChoice ?? readStoredMode()) return;
  applyMode(systemMode());
};

/**
 * Page restored from the back/forward cache: its DOM is a snapshot from before the
 * shopper may have switched on a later page, so re-resolve from storage.
 */
export const resyncThemeMode = () =>
  applyMode(readStoredMode() ?? manualChoice ?? systemMode());

const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
};

/**
 * Current mode, or `null` during SSR/hydration (the server cannot know the device
 * theme) — render mode-agnostic markup for `null` to stay hydration-safe.
 */
export const useThemeMode = () =>
  useSyncExternalStore(subscribe, getCurrentThemeMode, () => null);
